import { Heart, Zap, Sparkles, Star, Flame } from "lucide-react";

/** Decorative sticker — pure SVG-ish using lucide. */
export const Sticker = ({ kind = "heart", className = "", size = 56, color }) => {
  const map = {
    heart: { Icon: Heart, fill: color || "#ff2d8a", stroke: "#9d063e" },
    bolt: { Icon: Zap, fill: color || "#8a5cf6", stroke: "#5a2cc5" },
    spark: { Icon: Sparkles, fill: color || "#fbbf24", stroke: "#b45309" },
    star: { Icon: Star, fill: color || "#fbbf24", stroke: "#b45309" },
    flame: { Icon: Flame, fill: color || "#fb7185", stroke: "#9f1239" },
  };
  const { Icon, fill, stroke } = map[kind] || map.heart;
  return (
    <span className={`sticker-shadow inline-flex ${className}`} aria-hidden="true">
      <Icon
        width={size}
        height={size}
        strokeWidth={2.5}
        fill={fill}
        color={stroke}
      />
    </span>
  );
};

/** Hand-drawn scribble SVG used as background flair. */
export const Scribble = ({ className = "", color = "#ff2d8a" }) => (
  <svg
    viewBox="0 0 120 80"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 70 C 25 20, 55 80, 75 30 S 110 65, 115 20"
      fill="none"
      stroke={color}
      strokeWidth="6"
      strokeLinecap="round"
    />
  </svg>
);

/** The kawaii winking heart sticker hero element — star eye + tongue-out mouth. */
export const KawaiiHeart = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    <svg viewBox="0 0 200 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full sticker-shadow">
      <defs>
        <linearGradient id="kawaiiHeart" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff5fa3" />
          <stop offset="60%" stopColor="#ff2d8a" />
          <stop offset="100%" stopColor="#c41768" />
        </linearGradient>
        <linearGradient id="kawaiiShine" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>
      {/* Splash/burst lines above the heart (asset-sheet reference) */}
      <g stroke="#ff2d8a" strokeWidth="6" strokeLinecap="round" fill="none">
        <path d="M100 4 L100 22" />
        <path d="M78 12 L68 28" />
        <path d="M122 12 L132 28" />
        <path d="M58 20 L48 34" />
        <path d="M142 20 L152 34" />
      </g>
      {/* Heart body */}
      <path
        d="M100 195 C 32 150, 12 105, 28 70 C 44 38, 86 42, 100 76 C 114 42, 156 38, 172 70 C 188 105, 168 150, 100 195 Z"
        fill="url(#kawaiiHeart)"
        stroke="#fff"
        strokeWidth="8"
      />
      {/* Glossy highlight */}
      <ellipse cx="76" cy="96" rx="11" ry="16" fill="url(#kawaiiShine)" opacity="0.7" />
      <path
        d="M50 76 C 56 60, 78 56, 88 70"
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="8"
        strokeLinecap="round"
        fill="none"
      />
      {/* Star eye (left) */}
      <path
        d="M76 100 l5 -14 l5 14 l14 5 l-14 5 l-5 14 l-5 -14 l-14 -5 z"
        fill="#fff"
      />
      <circle cx="81" cy="105" r="3" fill="#3b0d22" />
      {/* Winking eye (right) */}
      <path
        d="M118 102 q12 -10 24 0"
        stroke="#fff"
        strokeWidth="7"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M118 98 l-4 -5" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
      <path d="M142 98 l4 -5" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
      {/* Open mouth with tongue */}
      <path
        d="M82 134 q18 26 36 0 Q 100 168 82 134 Z"
        fill="#3b0d22"
        stroke="#fff"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <ellipse cx="100" cy="154" rx="10" ry="6" fill="#ff85a8" />
    </svg>
  </div>
);

/** Winged heart sticker — top-right of the live preview card. */
export const WingedHeart = ({ size = 96, className = "" }) => (
  <div className={`inline-flex sticker-shadow ${className}`} style={{ width: size, height: size }} aria-hidden="true">
    <svg viewBox="0 0 220 200" width="100%" height="100%">
      <defs>
        <linearGradient id="wingedHeart" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff7eb6" />
          <stop offset="50%" stopColor="#ff2d8a" />
          <stop offset="100%" stopColor="#c41768" />
        </linearGradient>
      </defs>
      {/* Left wing */}
      <path d="M55 80 q-50 -10 -42 30 q 8 28 42 18 q 6 -18 0 -48 z"
        fill="#fff" stroke="#ff2d8a" strokeWidth="5" strokeLinejoin="round" />
      <path d="M22 92 q14 4 30 0" stroke="#ff2d8a" strokeWidth="3" fill="none" />
      <path d="M22 108 q14 4 28 0" stroke="#ff2d8a" strokeWidth="3" fill="none" />
      {/* Right wing */}
      <path d="M165 80 q50 -10 42 30 q -8 28 -42 18 q -6 -18 0 -48 z"
        fill="#fff" stroke="#ff2d8a" strokeWidth="5" strokeLinejoin="round" />
      <path d="M198 92 q-14 4 -30 0" stroke="#ff2d8a" strokeWidth="3" fill="none" />
      <path d="M198 108 q-14 4 -28 0" stroke="#ff2d8a" strokeWidth="3" fill="none" />
      {/* Heart body */}
      <path d="M110 178 C 50 140, 35 100, 50 70 C 65 42, 100 46, 110 75 C 120 46, 155 42, 170 70 C 185 100, 170 140, 110 178 Z"
        fill="url(#wingedHeart)" stroke="#fff" strokeWidth="8" />
      <ellipse cx="88" cy="88" rx="12" ry="18" fill="rgba(255,255,255,0.85)" />
    </svg>
  </div>
);

/** Chaos emoji — orange smiley with tongue, used on the Chaos Mode card. */
export const ChaosEmoji = ({ size = 96, className = "" }) => (
  <div className={`inline-flex sticker-shadow ${className}`} style={{ width: size, height: size }} aria-hidden="true">
    <svg viewBox="0 0 200 200" width="100%" height="100%">
      <defs>
        <radialGradient id="chaosBody" cx="0.4" cy="0.35" r="0.7">
          <stop offset="0%" stopColor="#ffd07a" />
          <stop offset="55%" stopColor="#ff8a3d" />
          <stop offset="100%" stopColor="#e25a00" />
        </radialGradient>
      </defs>
      <circle cx="100" cy="100" r="86" fill="url(#chaosBody)" stroke="#fff" strokeWidth="8" />
      {/* Eyes (crossed-out X shape for chaos) */}
      <g stroke="#3b0d22" strokeWidth="9" strokeLinecap="round">
        <path d="M62 78 l18 18 M80 78 l-18 18" />
        <path d="M120 78 l18 18 M138 78 l-18 18" />
      </g>
      {/* Open laughing mouth */}
      <path d="M62 122 q38 50 76 0 Q 100 168 62 122 Z"
        fill="#3b0d22" stroke="#fff" strokeWidth="4" strokeLinejoin="round" />
      {/* Tongue out */}
      <path d="M88 152 q12 22 24 0 q -2 18 -12 18 q -10 0 -12 -18 z"
        fill="#ff5fa3" stroke="#fff" strokeWidth="3" />
      {/* Highlight blush */}
      <circle cx="56" cy="124" r="10" fill="rgba(255,140,140,0.55)" />
      <circle cx="144" cy="124" r="10" fill="rgba(255,140,140,0.55)" />
    </svg>
  </div>
);

/** Purple coil/spiral — a chaotic decorative accent. */
export const PurpleCoil = ({ className = "", color = "#8a5cf6" }) => (
  <svg viewBox="0 0 140 140" className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M70 14 a 56 56 0 1 1 -42 90 a 42 42 0 1 1 70 -8 a 30 30 0 1 1 -52 -4 a 20 20 0 1 1 32 -4 a 10 10 0 1 1 -18 0"
      fill="none"
      stroke={color}
      strokeWidth="6"
      strokeLinecap="round"
    />
  </svg>
);
